/**
 * Defaults and shared shape for the business card form.
 *
 * Separate from the components in `components/businessCards.tsx` purely so that
 * file exports components only — a module mixing the two breaks React fast
 * refresh, which this project treats as a lint error rather than a warning.
 *
 * On defaults: a card used to start completely blank, so creating the very first
 * company card meant retyping the company details that are already stored under
 * Company Info. The two kinds of card seed from the two different places that
 * actually know the answer — a company card from Company Info, a personal card
 * from your own profile.
 */
import type { BusinessCardTemplate, NotificationSettings, User } from '../types'

export interface CardFormState {
  name: string
  sender_name: string
  title: string
  email: string
  phone: string
  greeting_text: string
}

export const EMPTY_FORM: CardFormState = {
  name: '',
  sender_name: '',
  title: '',
  email: '',
  phone: '',
  greeting_text: '',
}

/**
 * A company card pre-filled from Company Info.
 *
 * Every field stays editable — this is a starting point, not a lock. The card
 * name gets a distinguishing suffix because it is an internal label and two
 * cards both called "Action Communications" are impossible to tell apart in the
 * send picker.
 */
export function companyCardDefaults(settings: NotificationSettings | null): CardFormState {
  if (!settings) return EMPTY_FORM
  return {
    ...EMPTY_FORM,
    name: settings.company_name ? `${settings.company_name} — Main` : '',
    sender_name: settings.company_name ?? '',
    email: settings.company_email ?? '',
    phone: settings.company_phone ?? '',
  }
}

/** A personal card pre-filled from the signed-in user's own profile. */
export function personalCardDefaults(user: User | null): CardFormState {
  if (!user) return EMPTY_FORM
  const name = user.full_name ?? ''
  return {
    ...EMPTY_FORM,
    name: name ? `${name} — Personal` : '',
    sender_name: name,
    email: user.email ?? '',
    phone: user.phone ?? '',
  }
}

/**
 * Which card should be selected when the send modal opens.
 *
 * Prefers a personal card the signed-in user owns, then any company card, then
 * whatever is left. Admins see every user's personal cards in that list, so
 * "the first personal card" is not necessarily one of theirs — hence the
 * ownership check rather than a bare type check.
 *
 * The API returns the list ordered; without that this rule would still pick an
 * arbitrary card whenever a user has more than one of a kind.
 */
export function pickDefaultCard(
  cards: BusinessCardTemplate[],
  userId?: number,
): BusinessCardTemplate | null {
  const own = cards.find((c) => c.card_type === 'personal' && c.owner_user_id === userId)
  if (own) return own
  const company = cards.find((c) => c.card_type === 'company')
  if (company) return company
  return cards[0] ?? null
}
